import { useEffect, useRef, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type AdminTabRailItem = {
  id: string;
  label: string;
  icon?: LucideIcon;
  emoji?: string;
  badge?: number | string;
  disabled?: boolean;
};

type Props = {
  items: AdminTabRailItem[];
  value: string;
  onChange: (id: string) => void;
  className?: string;
  /** Компактные чипы с эмодзи (каталог: Каталог / Пульты / …) */
  variant?: 'main' | 'compact';
  /** Подсказка под полосой */
  hint?: string;
};

export function AdminHorizontalTabRail({
  items,
  value,
  onChange,
  className,
  variant = 'main',
  hint,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    const el = chipRefs.current[value];
    if (!el || !scrollRef.current) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [value, items.length]);

  const isCompact = variant === 'compact';

  return (
    <div className={cn('w-full min-w-0', className)}>
      <div
        className="rounded-xl p-1"
        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-subtle)' }}
      >
        <div
          ref={scrollRef}
          role="tablist"
          className={cn(
            'flex gap-1.5 overflow-x-auto no-scrollbar scroll-smooth snap-x snap-mandatory touch-pan-x',
            isCompact ? 'py-0.5 px-0.5' : 'py-0.5 px-0.5',
          )}
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {items.map((item) => {
            const active = value === item.id;
            const Icon = item.icon;
            let badgeNode: ReactNode = null;
            if (item.badge != null && item.badge !== '' && Number(item.badge) !== 0) {
              const n = typeof item.badge === 'number' ? item.badge : item.badge;
              badgeNode = (
                <span
                  className="absolute -top-1 -right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full text-[9px] font-black leading-none"
                  style={{ background: '#FC814A', color: '#fff' }}
                >
                  {typeof n === 'number' && n > 99 ? '99+' : n}
                </span>
              );
            }

            return (
              <button
                key={item.id}
                ref={(node) => {
                  chipRefs.current[item.id] = node;
                }}
                type="button"
                role="tab"
                aria-selected={active}
                disabled={item.disabled}
                onClick={() => onChange(item.id)}
                className={cn(
                  'press relative shrink-0 snap-start touch-manipulation transition-all',
                  isCompact
                    ? 'flex flex-col items-center gap-1 px-3 py-2.5 rounded-xl text-[11px] font-bold min-w-[4.5rem]'
                    : 'flex items-center justify-center gap-1.5 px-3.5 py-2.5 rounded-lg text-xs font-bold min-h-[44px] whitespace-nowrap',
                  item.disabled && 'opacity-40 pointer-events-none',
                )}
                style={
                  active
                    ? {
                        background: 'rgba(99,179,237,0.2)',
                        color: 'var(--cyan-bright)',
                        border: '1px solid var(--cyan-border)',
                      }
                    : { color: 'var(--text-3)', border: '1px solid transparent' }
                }
              >
                {item.emoji ? (
                  <span className={cn('leading-none', isCompact ? 'text-lg' : 'text-base')}>{item.emoji}</span>
                ) : null}
                {Icon ? <Icon size={isCompact ? 14 : 13} className="shrink-0" aria-hidden /> : null}
                <span className={isCompact ? 'text-center leading-tight' : undefined}>{item.label}</span>
                {badgeNode}
              </button>
            );
          })}
        </div>
      </div>
      {hint ? (
        <p className="mt-1.5 px-0.5 text-[10px] leading-snug" style={{ color: 'var(--text-3)' }}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}
